import { Input } from '@tempo-io/sui-input';
import { type ReactNode, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseDialog } from '../base-dialog';

export const DeleteDialog = ({
  open,
  onOpenChange,
  onConfirm,
  isPending,
  title,
  description,
  confirmLabel,
  cancelLabel,
  confirmationValue,
  confirmationLabel,
  'data-testid': dataTestId,
  confirmButtonTestId,
  cancelButtonTestId,
}: {
  open: boolean;
  onOpenChange: (isDialogOpen: boolean) => void;
  onConfirm: () => void | Promise<void>;
  isPending?: boolean;
  title: string;
  description: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmationValue?: string;
  confirmationLabel?: ReactNode;
  'data-testid'?: string;
  confirmButtonTestId?: string;
  cancelButtonTestId?: string;
}) => {
  const { t } = useTranslation();
  const confirmationLabelId = useId();
  const [confirmationInput, setConfirmationInput] = useState('');

  useEffect(() => {
    if (!open) {
      setConfirmationInput('');
    }
  }, [open]);
  const confirmationRequired = confirmationValue !== undefined;
  const confirmationSatisfied = !confirmationRequired || confirmationInput === confirmationValue;

  return (
    <BaseDialog
      data-testid={dataTestId}
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      intent="destructive"
      size="sm"
      isPending={isPending}
      primaryAction={{
        label: confirmLabel ?? t(($) => $.common.delete),
        onClick: onConfirm,
        disabled: !confirmationSatisfied || Boolean(isPending),
        'data-testid': confirmButtonTestId,
      }}
      secondaryAction={{
        label: cancelLabel ?? t(($) => $.common.cancel),
        onClick: () => onOpenChange(false),
        'data-testid': cancelButtonTestId,
      }}
    >
      {confirmationRequired && (
        <div className="mt-4">
          <p id={confirmationLabelId} className="mb-2 text-sm text-text-neutral-secondary">
            {confirmationLabel ??
              t(($) => $.common.deleteDialog.typeToConfirm, { value: confirmationValue })}
          </p>
          <Input
            autoFocus
            value={confirmationInput}
            onChange={(e) => setConfirmationInput(e.target.value)}
            disabled={isPending}
            placeholder={confirmationValue}
            aria-describedby={confirmationLabelId}
            autoComplete="off"
            data-testid="delete-confirmation-input"
          />
        </div>
      )}
    </BaseDialog>
  );
};
